/* Swipeable image carousel for product cards on collection / search / grids.
   Initializes every .variant-card-carousel on the page, is idempotent, and
   re-scans when the grid is swapped out by filtering / pagination.
   Works on both mobile (touch) and desktop (drag + arrows). */
(function () {
  if (window.__variantCardCarouselInit) return;
  window.__variantCardCarouselInit = true;

  var MOVE_THRESHOLD = 10; // px before we decide swipe vs scroll/tap
  var DIRECTION_RATIO = 1.2; // horizontal must beat vertical by this to count as a swipe
  var DISTANCE_RATIO = 0.18; // fraction of slide width to advance
  var VELOCITY = 0.3; // px/ms fling threshold
  var MIN_DISTANCE = 40;

  // Shared observer: when a card scrolls near the viewport, prefetch its 2nd image
  // (low priority) so the first swipe is instant. The rest load on first touch.
  var prefetchObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            for (var i = 0; i < entries.length; i++) {
              if (entries[i].isIntersecting) {
                var el = entries[i].target;
                prefetchObserver.unobserve(el);
                if (el.__vccPrefetch) el.__vccPrefetch();
              }
            }
          },
          { rootMargin: '300px 0px' }
        )
      : null;

  function loadDeferredImg(img, priority) {
    var src = img.getAttribute('data-vcc-src');
    if (!src) return;
    if (priority) {
      try { img.fetchPriority = priority; } catch (e) {}
    }
    img.src = src;
    img.removeAttribute('data-vcc-src');
  }

  function initCarousel(root) {
    if (!root || root.dataset.vccInit === '1') return;
    var track = root.querySelector('.vcc-track');
    if (!track) return;
    var slides = Array.prototype.slice.call(track.children);
    if (slides.length <= 1) {
      root.dataset.vccInit = '1';
      return; // nothing to swipe
    }
    root.dataset.vccInit = '1';

    var prevBtn = root.querySelector('.vcc-prev');
    var nextBtn = root.querySelector('.vcc-next');
    var index = 0;

    // pointer state
    var dragging = false;
    var startX = 0;
    var startY = 0;
    var dx = 0;
    var dy = 0;
    var horizontal = null;
    var startTime = 0;
    var didSwipe = false;

    // ---- Intent-based image loading ----
    function loadSlide(i, priority) {
      var s = slides[i];
      if (!s) return;
      var imgs = s.querySelectorAll('img[data-vcc-src]');
      for (var k = 0; k < imgs.length; k++) loadDeferredImg(imgs[k], priority);
    }

    var allLoaded = false;
    function loadAllOnce() {
      if (allLoaded) return;
      allLoaded = true;
      for (var i = 1; i < slides.length; i++) loadSlide(i);
    }

    // Prefetch only the 2nd image when the card scrolls into view (low priority).
    root.__vccPrefetch = function () { loadSlide(1, 'low'); };
    if (prefetchObserver) {
      prefetchObserver.observe(root);
    } else {
      root.__vccPrefetch();
    }

    // Desktop: hovering a card preloads the rest so arrow clicks are instant.
    root.addEventListener('mouseenter', loadAllOnce);

    function update() {
      var w = slides[0].offsetWidth || track.offsetWidth;
      track.style.transition = 'transform 0.35s ease';
      track.style.transform = 'translateX(' + -(w * index) + 'px)';
      if (prevBtn) prevBtn.disabled = index === 0;
      if (nextBtn) nextBtn.disabled = index === slides.length - 1;
    }

    function onDown(e) {
      loadAllOnce(); // user intends to swipe — load remaining images now
      dragging = true;
      didSwipe = false;
      startX = e.clientX;
      startY = e.clientY;
      dx = 0;
      dy = 0;
      horizontal = null;
      startTime = Date.now();
      track.style.transition = 'none';
      try { track.setPointerCapture(e.pointerId); } catch (err) {}
    }

    function onMove(e) {
      if (!dragging) return;
      dx = e.clientX - startX;
      dy = e.clientY - startY;

      if (horizontal === null) {
        if (Math.abs(dx) < MOVE_THRESHOLD && Math.abs(dy) < MOVE_THRESHOLD) return;
        horizontal = Math.abs(dx) > Math.abs(dy) * DIRECTION_RATIO;
        if (!horizontal) {
          // vertical scroll — let the page take over
          dragging = false;
          try { track.releasePointerCapture(e.pointerId); } catch (err) {}
          return;
        }
      }
      e.preventDefault();
      didSwipe = true;
      var w = slides[0].offsetWidth || track.offsetWidth;
      track.style.transform = 'translateX(' + (-(w * index) + dx) + 'px)';
    }

    function onUp(e) {
      if (!dragging) return;
      dragging = false;
      var w = slides[0].offsetWidth || track.offsetWidth;
      var elapsed = Math.max(1, Date.now() - startTime);
      var velocity = dx / elapsed;
      var thresh = Math.max(MIN_DISTANCE, w * DISTANCE_RATIO);

      if (horizontal) {
        if (dx > thresh || velocity > VELOCITY) {
          index = Math.max(0, index - 1);
        } else if (dx < -thresh || velocity < -VELOCITY) {
          index = Math.min(slides.length - 1, index + 1);
        }
      }
      update();
      try { track.releasePointerCapture(e.pointerId); } catch (err) {}
    }

    track.addEventListener('pointerdown', onDown);
    track.addEventListener('pointermove', onMove, { passive: false });
    track.addEventListener('pointerup', onUp);
    track.addEventListener('pointercancel', function () { dragging = false; });

    // A swipe's trailing click must be cancelled so the card doesn't navigate.
    // A plain tap/click never reaches the underlying <a class="js-prod-link">
    // on its own — this carousel sits absolutely positioned on top of it
    // (z-index: 2) — so a non-swipe click has to be forwarded manually.
    // Clicks on the prev/next arrows are excluded — they have their own
    // handlers below and must never trigger navigation.
    root.addEventListener(
      'click',
      function (e) {
        if (didSwipe) {
          e.preventDefault();
          e.stopPropagation();
          didSwipe = false;
          return;
        }
        if (e.target.closest('.vcc-arrow')) return;
        var link = root.parentElement && root.parentElement.querySelector('.js-prod-link');
        if (!link) return;
        var href = link.getAttribute('href');
        if (!href) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) {
          window.open(href, '_blank');
        } else {
          window.location.href = href;
        }
      },
      true
    );

    // Arrows step through images without navigating to the product.
    if (prevBtn) {
      prevBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        loadAllOnce();
        if (index > 0) {
          index--;
          update();
        }
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        loadAllOnce();
        if (index < slides.length - 1) {
          index++;
          update();
        }
      });
    }

    update();
    window.addEventListener('resize', update);
  }

  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll('.variant-card-carousel');
    for (var i = 0; i < nodes.length; i++) initCarousel(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Re-scan when filters / pagination swap the grid HTML.
  var scheduled = false;
  var observer = new MutationObserver(function () {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      initAll();
    });
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();